// src/lib/posts/sections.ts
//
// One home for the rule that decides which section a post category belongs to.
// The rule used to live twice, once in the create picker and once on the metro
// browse page, and both copies collapsed the `looking` type into "Now hiring"
// by matching gendered slugs (/^look[mw]/ and /^available-(male|female)-talent/).
//
// That was wrong, and it worked against the poster. The JuicyStarz taxonomy has
// four types and they mean four different things:
//
//   connect   collab-*     a collaboration offer between peers
//   hiring    casting-*    a job opening: a producer casting or booking talent
//   looking   available-*  a performer or crew member advertising themselves
//   services  srv-*        crew and vendor work offered or wanted
//
// Filing `available-*` under "Now hiring" told a performer advertising her own
// availability that she was posting a paid opportunity, and it diluted the
// hiring section with self-advertisements. The distinction is the same one
// vault/decisions/2026-08-27-jobposting-scoped-to-casting-posts.md rests on:
// only casting-* is a job opening, which is why JobPosting JSON-LD is emitted
// for casting-* and nothing else.
//
// So each type now gets its own section and the slug regexes are gone.

export type PostSection = 'connect' | 'hiring' | 'looking' | 'services';

export interface PostSectionMeta {
  /** Matches Category.type from the JSTARZ `Categories` table. */
  key: PostSection;
  /**
   * DOM id suffix on the metro browse page (`type-${anchor}`). /jobs-hub deep
   * links reach a section through these, so an anchor is a URL contract. The
   * map from a ?type= value to one of these lives just below, in this file, so
   * renaming an anchor cannot leave a caller pointing at an id that is gone.
   */
  anchor: string;
  label: string;
  /** One line under the label on the create picker. Speaks to the poster. */
  hint: string;
  /** One line under the heading on the metro browse page. Speaks to the reader. */
  subtitle: string;
}

export const POST_SECTIONS: readonly PostSectionMeta[] = [
  {
    key: 'connect',
    anchor: 'collaborate',
    label: 'Collaborate',
    hint: 'Connect with peers, find collaborators, build your network.',
    subtitle: 'Members looking for talent partners',
  },
  {
    key: 'hiring',
    anchor: 'now-hiring',
    label: 'Now hiring',
    hint: 'Cast a role, book talent, or post a paid opportunity.',
    subtitle: 'Producers and studios offering paid work',
  },
  {
    key: 'looking',
    anchor: 'available',
    label: 'Available for work',
    hint: 'Advertise your own availability so producers can find and book you.',
    subtitle: 'Performers and crew advertising their availability',
  },
  {
    key: 'services',
    anchor: 'services',
    label: 'Services',
    hint: 'Offer or look for production, styling, photo, or post help.',
    subtitle: 'Crew, support, and creator services',
  },
] as const;

/** The shape both callers share. `type` comes from /api/categories. */
export interface CategoryLike {
  slug: string;
  name?: string;
  type?: string;
}

const SECTION_KEYS: readonly PostSection[] = ['connect', 'hiring', 'looking', 'services'];

/**
 * Slug fallback for rows whose `type` came back empty. The taxonomy is stable,
 * so this is a repair path rather than a second rule: every live category
 * carries a type. Anything still unrecognized returns null and appears in no
 * section, which is the pre-existing behavior on both surfaces and is easier to
 * spot than a row quietly filed under the wrong heading.
 */
function sectionFromSlug(slug: string): PostSection | null {
  const s = String(slug || '').toLowerCase();
  if (!s) return null;
  if (s.startsWith('collab') || /^[mwc]4[mwc]/.test(s)) return 'connect';
  if (s.startsWith('casting') || s.startsWith('hiring')) return 'hiring';
  if (s.startsWith('available') || s.startsWith('look')) return 'looking';
  if (s.startsWith('srv')) return 'services';
  return null;
}

/** The section a category belongs to, or null when it belongs to none. */
export function sectionForCategory(cat: CategoryLike): PostSection | null {
  const t = String(cat?.type || '').trim().toLowerCase();
  if ((SECTION_KEYS as readonly string[]).includes(t)) return t as PostSection;
  return sectionFromSlug(cat?.slug);
}

/** Every category in one section, order preserved. */
export function categoriesInSection<T extends CategoryLike>(cats: T[], section: PostSection): T[] {
  if (!Array.isArray(cats)) return [];
  return cats.filter((c) => sectionForCategory(c) === section);
}

/**
 * /jobs-hub links to /posts?type=hiring|looking|connect|services. PostsLanding
 * turns that into the metro page's section anchor, which it relays through
 * sessionStorage and appends to the redirect as a hash.
 *
 * This lives here, beside the anchors themselves, because it used to live in
 * src/app/posts/ClientPage.tsx: a file nothing imported. The bucket landing was
 * written into it on 2026-08-10, six weeks after /posts/page.tsx had stopped
 * rendering it, so the four jobs-hub CTAs never once reached a section. Keeping
 * the map next to the ids means a rename cannot orphan the lookup again.
 */
export function anchorForTypeParam(type: string | null | undefined): string | null {
  const t = String(type ?? '').trim().toLowerCase();
  const hit = POST_SECTIONS.find((s) => s.key === t);
  return hit ? `type-${hit.anchor}` : null;
}
